import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin, useSuperAdmin } from '../../lib/auth';
import { authed, absUrl } from '../../lib/api';
import { formatMoney } from '../../lib/utils';
import AdminShell from './AdminShell';
import { OrderDetailsModal } from '../../components/OrderDetailsModal';
import { OrderStatusBadge, PaymentStatusBadge, isOverdueCollection, formatOverdueAge } from '../../components/StatusBadges';
import { ChangeMyPasswordCard, ChangeMyEmailCard, ChangeEmployeePasswordCard, ChangeEmployeeEmailCard } from '../../components/CredentialCards';
import { ImagePickerButton } from '../../components/ImagePickerButton';
import { Check, X, Upload, Trash2, Edit3, Plus, ChevronUp, ChevronDown, Layers, Image as ImageIcon, RotateCcw } from 'lucide-react';

const BASE_TABS = [
    { key: 'hero', label: 'Hero Slides' },
    { key: 'ads', label: 'Ad Videos' },
    { key: 'products', label: 'Products' },
    { key: 'city', label: 'City Pricing' },
    { key: 'distributors', label: 'Distributors' },
    { key: 'orders', label: 'Orders' },
    { key: 'categories', label: 'Categories' },
    { key: 'settings', label: 'Settings' },
    { key: 'audit', label: 'Audit Log' },
];
const SUPER_ONLY_TABS = [
    { key: 'admins', label: 'Admin Users' },
    { key: 'security', label: 'Security' },
];

export default function AdminDashboard({ mode = 'admin' }) {
    // mode: 'admin' | 'super_admin'
    const nav = useNavigate();
    const { admin, signOut: signOutAdmin } = useAdmin();
    const { superAdmin, signOut: signOutSA } = useSuperAdmin();
    const user = mode === 'admin' ? admin : superAdmin;
    const realm = mode === 'admin' ? 'admin' : 'super_admin';
    const [tab, setTab] = useState('hero');
    const [totpEnabled, setTotpEnabled] = useState(true);
    const [showTotpBanner, setShowTotpBanner] = useState(false);

    const tabs = useMemo(() => mode === 'super_admin' ? [...BASE_TABS, ...SUPER_ONLY_TABS] : BASE_TABS, [mode]);

    useEffect(() => {
        if (mode !== 'super_admin') return;
        (async () => {
            try {
                const r = await authed('super_admin', { method: 'GET', url: '/superadmin/me' });
                setTotpEnabled(!!r.data.totp_enabled);
                setShowTotpBanner(!r.data.totp_enabled);
            } catch (e) { /* ignore */ }
        })();
    }, [mode]);

    const signOut = () => {
        if (mode === 'admin') { signOutAdmin(); nav('/admin', { replace: true }); }
        else { signOutSA(); nav('/secure-admin-login', { replace: true }); }
    };

    return (
        <AdminShell
            title={mode === 'super_admin' ? 'Super Admin Dashboard' : 'Admin Dashboard'}
            tabs={tabs} activeTab={tab} onTab={setTab}
        >
            {mode === 'super_admin' && showTotpBanner && (
                <div className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="totp-warning-banner">
                    <div className="flex-1">
                        <div className="text-[10px] font-bold text-amber-900 tracking-widest">SECURITY NOTICE</div>
                        <div className="mt-1 text-sm text-amber-900">For enhanced security, Google Authenticator has not been configured yet. Please complete the setup from <button className="font-bold underline" onClick={() => setTab('security')}>Settings &rarr; Security</button>.</div>
                    </div>
                    <button onClick={() => setShowTotpBanner(false)} className="text-amber-900 font-bold text-sm">Dismiss</button>
                </div>
            )}
            {tab === 'hero' && <HeroSlidesTab realm={realm} />}
            {tab === 'ads' && <AdVideosTab realm={realm} />}
            {tab === 'products' && <ProductsTab realm={realm} />}
            {tab === 'city' && <CityPricingTab realm={realm} />}
            {tab === 'distributors' && <DistributorsTab realm={realm} mode={mode} />}
            {tab === 'orders' && <OrdersTab realm={realm} />}
            {tab === 'categories' && <CategoriesTab realm={realm} />}
            {tab === 'settings' && <SettingsTab realm={realm} mode={mode} />}
            {tab === 'audit' && <AuditTab realm={realm} />}
            {tab === 'admins' && mode === 'super_admin' && <AdminsMgmtTab />}
            {tab === 'security' && mode === 'super_admin' && <SecurityTab onEnabledChange={(v) => { setTotpEnabled(v); if (v) setShowTotpBanner(false); }} />}
        </AdminShell>
    );
}

// -------- Shared helpers --------
function useList(realm, url, deps = []) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const reload = useCallback(async () => {
        setLoading(true);
        try { const r = await authed(realm, { method: 'GET', url }); setData(r.data); }
        catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [realm, url]);
    useEffect(() => { reload(); }, deps);
    return { data, loading, reload };
}

function Section({ title, children, actions }) {
    return (
        <div className="card-soft p-5">
            <div className="flex items-center justify-between"><h2 className="font-extrabold text-lg">{title}</h2>{actions}</div>
            <div className="mt-3">{children}</div>
        </div>
    );
}

function Input({ label, type = 'text', value, onChange, tid, step, min }) {
    const numericProps = type === 'number' ? { step: step ?? '0.01', min: min ?? '0', inputMode: 'decimal' } : {};
    return (
        <div>
            {label && <label className="text-[10px] font-bold text-sdf-text-2 tracking-widest">{label.toUpperCase()}</label>}
            <input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full border border-sdf-line rounded-xl px-3 py-2 outline-none text-sm bg-white" data-testid={tid} {...numericProps} />
        </div>
    );
}

/**
 * Small paired Up / Down reorder buttons. Each click POSTs to `${basePath}/{id}/reorder?direction=up|down`
 * and calls `onDone` to reload the list. Save is immediate.
 */
function ReorderButtons({ realm, basePath, id, onDone, testIdPrefix = 'reorder' }) {
    const [busy, setBusy] = useState(false);
    const move = async (direction) => {
        if (busy) return;
        setBusy(true);
        try {
            await authed(realm, { method: 'POST', url: `${basePath}/${id}/reorder?direction=${direction}` });
            onDone?.();
        } catch (e) { /* ignore, surface via reload */ }
        finally { setBusy(false); }
    };
    return (
        <div className="flex flex-col gap-0.5">
            <button
                onClick={() => move('up')}
                disabled={busy}
                className="w-7 h-6 rounded-md border border-sdf-line bg-white hover:bg-sdf-cream/40 flex items-center justify-center disabled:opacity-50"
                aria-label="Move up"
                data-testid={`${testIdPrefix}-up-${id}`}
            ><ChevronUp size={12} /></button>
            <button
                onClick={() => move('down')}
                disabled={busy}
                className="w-7 h-6 rounded-md border border-sdf-line bg-white hover:bg-sdf-cream/40 flex items-center justify-center disabled:opacity-50"
                aria-label="Move down"
                data-testid={`${testIdPrefix}-down-${id}`}
            ><ChevronDown size={12} /></button>
        </div>
    );
}

async function uploadFile(realm, file, subdir = 'general') {
    const fd = new FormData();
    fd.append('file', file);
    const r = await authed(realm, { method: 'POST', url: `/admin/uploads?subdir=${encodeURIComponent(subdir)}`, data: fd, headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
}

// -------- Tabs --------
function HeroSlidesTab({ realm }) {
    const { data, loading, reload } = useList(realm, '/admin/hero-slides');
    const [caption, setCaption] = useState('');
    const [url, setUrl] = useState('');
    const [device, setDevice] = useState('both');

    const add = async () => {
        if (!url) return;
        await authed(realm, { method: 'POST', url: '/admin/hero-slides', data: { image_url: url, caption, sort_order: data.length + 1, active: true, device } });
        setCaption(''); setUrl(''); setDevice('both'); reload();
    };
    const del = async (id) => { await authed(realm, { method: 'DELETE', url: `/admin/hero-slides/${id}` }); reload(); };
    return (
        <Section title="Hero Slides">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                    <div className="font-bold text-sm">Add new slide</div>
                    <ImagePickerButton
                        realm={realm}
                        subdir="hero"
                        label={url ? 'Change Image' : 'Upload Image'}
                        onPicked={(newUrl) => setUrl(newUrl)}
                        testIdPrefix="hero"
                    />
                    {url && <div className="text-xs">Selected: <a href={absUrl(url)} target="_blank" rel="noreferrer" className="text-sdf-red underline">preview</a></div>}
                    <Input label="Caption" value={caption} onChange={setCaption} tid="hero-caption" />
                    <div>
                        <label className="text-[10px] font-bold text-sdf-text-2 tracking-widest">DEVICE</label>
                        <select value={device} onChange={(e) => setDevice(e.target.value)} className="mt-1.5 w-full border border-sdf-line rounded-xl px-3 py-2 outline-none text-sm bg-white">
                            <option value="both">Both (Mobile + Desktop)</option>
                            <option value="mobile">Mobile only</option>
                            <option value="desktop">Desktop only</option>
                        </select>
                    </div>
                    <button onClick={add} disabled={!url} className="btn-red" data-testid="hero-add"><Plus size={14} /> Add Slide</button>
                </div>
                <div className="space-y-3">
                    <div className="font-bold text-sm">Existing slides ({data.length})</div>
                    {loading ? <div className="spinner"/> : data.map((s) => (
                        <div key={s.id} className="card-soft p-2 flex items-center gap-3">
                            <ReorderButtons realm={realm} basePath="/admin/hero-slides" id={s.id} onDone={reload} testIdPrefix="hero-reorder" />
                            <img src={absUrl(s.image_url)} alt="" className="w-24 h-16 object-cover rounded-lg bg-neutral-100" />
                            <div className="flex-1 text-sm">
                                <div>{s.caption || <span className="text-sdf-text-2">no caption</span>}</div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-sdf-text-2">{s.device || 'both'} · order {s.sort_order}</div>
                            </div>
                            <button onClick={() => del(s.id)} className="p-2 text-red-600"><Trash2 size={16} /></button>
                        </div>
                    ))}
                </div>
            </div>
        </Section>
    );
}

function AdVideosTab({ realm }) {
    const { data, loading, reload } = useList(realm, '/admin/ad-videos');
    const [caption, setCaption] = useState('');
    const [url, setUrl] = useState('');
    const [urlManual, setUrlManual] = useState('');
    const [busy, setBusy] = useState(false);
    const onFile = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        setBusy(true);
        try { const up = await uploadFile(realm, f, 'ads'); setUrl(up.url); } finally { setBusy(false); }
    };
    const add = async () => {
        const src = url || urlManual;
        if (!src) return;
        await authed(realm, { method: 'POST', url: '/admin/ad-videos', data: { video_url: src, caption, sort_order: data.length + 1, active: true } });
        setCaption(''); setUrl(''); setUrlManual(''); reload();
    };
    const del = async (id) => { await authed(realm, { method: 'DELETE', url: `/admin/ad-videos/${id}` }); reload(); };
    return (
        <Section title="Advertisement Videos">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                    <label className="btn-outline cursor-pointer w-fit"><Upload size={14} /> Upload MP4 (max 40MB)<input type="file" accept="video/*" className="hidden" onChange={onFile} data-testid="ad-file" /></label>
                    {url && <div className="text-xs text-green-700">Uploaded to local storage.</div>}
                    <Input label="Or paste hosted video URL" value={urlManual} onChange={setUrlManual} />
                    <Input label="Caption" value={caption} onChange={setCaption} />
                    <button onClick={add} disabled={busy || (!url && !urlManual)} className="btn-red" data-testid="ad-add"><Plus size={14} /> Add Video</button>
                </div>
                <div className="space-y-3">
                    {loading ? <div className="spinner"/> : data.map((v) => (
                        <div key={v.id} className="card-soft p-2 flex items-center gap-3">
                            <ReorderButtons realm={realm} basePath="/admin/ad-videos" id={v.id} onDone={reload} testIdPrefix="ad-reorder" />
                            <video src={absUrl(v.video_url)} className="w-24 h-16 object-cover rounded-lg bg-black" muted />
                            <div className="flex-1 text-sm truncate">{v.caption || v.video_url}</div>
                            <button onClick={() => del(v.id)} className="p-2 text-red-600"><Trash2 size={16} /></button>
                        </div>
                    ))}
                </div>
            </div>
        </Section>
    );
}

function ProductsTab({ realm }) {
    const cats = useList(realm, '/admin/categories');
    const prods = useList(realm, '/admin/products');
    const [selectedCat, setSelectedCat] = useState('');
    const [creating, setCreating] = useState(false);

    // Auto-select first category
    useEffect(() => {
        if (!selectedCat && cats.data.length) setSelectedCat(cats.data[0].slug);
    }, [cats.data, selectedCat]);

    const categoryProducts = prods.data.filter(p => p.category_slug === selectedCat);
    const currentCat = cats.data.find(c => c.slug === selectedCat);
    // "One product per category" model matching Retailers: pick the first product or create.
    const activeProduct = categoryProducts[0];

    const createProduct = async () => {
        if (!currentCat) return;
        await authed(realm, {
            method: 'POST', url: '/admin/products',
            data: {
                name: currentCat.name, category_slug: currentCat.slug,
                description: '', image_url: '', sort_order: 1, active: true,
                variants: [],
            },
        });
        setCreating(false); prods.reload();
    };

    return (
        <Section title="Products">
            {/* Category pills (top) */}
            <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-cat-pills">
                {cats.data.map(c => (
                    <button
                        key={c.slug}
                        onClick={() => setSelectedCat(c.slug)}
                        className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${selectedCat === c.slug ? 'bg-sdf-red text-white border-sdf-red shadow-sm' : 'bg-white text-sdf-text border-sdf-line hover:bg-neutral-50'}`}
                        data-testid={`admin-cat-${c.slug}`}
                    >
                        {c.name}
                    </button>
                ))}
            </div>

            {/* Header: title + subtitle */}
            <div className="mb-4">
                <h2 className="text-2xl font-extrabold text-sdf-text">{currentCat?.name || 'Products'}</h2>
                <p className="text-xs text-sdf-text-2 mt-1">Manage flavours weight-wise. Add/edit/remove/reorder per weight group.</p>
            </div>

            {!activeProduct ? (
                <div className="card-soft p-8 text-center">
                    <div className="text-sdf-text font-bold">No product set up for {currentCat?.name} yet.</div>
                    <p className="text-xs text-sdf-text-2 mt-1">Create the product to start adding weight variants and flavours.</p>
                    <button onClick={createProduct} className="btn-red mt-4 inline-flex" data-testid="create-cat-product"><Plus size={14} /> Create {currentCat?.name} product</button>
                </div>
            ) : (
                <WeightGroupsManager realm={realm} product={activeProduct} onReload={prods.reload} categoryName={currentCat?.name} />
            )}
        </Section>
    );
}

function WeightGroupsManager({ realm, product, onReload, categoryName }) {
    const [variants, setVariants] = useState(product.variants || []);
    const [addingVariant, setAddingVariant] = useState(false);
    const [newVar, setNewVar] = useState({ size_label: '', moq: 1, price: 0 });

    // ---- Flavour list lifted to the parent so it uses the SAME
    // architecture as Hero Slides / Ad Videos: a single source of truth
    // via useList, refreshed after every mutation. Eliminates per-card
    // local state and any potential race between mount-load and mutation
    // reload that was causing the reorder-then-revert bug in production. ----
    const flavoursList = useList(realm, `/admin/products/${product.id}/flavours`, [product.id]);

    useEffect(() => { setVariants(product.variants || []); }, [product]);

    const persistVariants = async (next) => {
        setVariants(next);
        await authed(realm, { method: 'PUT', url: `/admin/products/${product.id}`, data: { ...product, variants: next } });
        onReload();
    };
    const moveVar = async (idx, delta) => {
        const next = [...variants];
        const swap = idx + delta;
        if (swap < 0 || swap >= next.length) return;
        [next[idx], next[swap]] = [next[swap], next[idx]];
        next.forEach((v, i) => { v.sort_order = i + 1; });
        await persistVariants(next);
    };
    const removeVar = async (id) => {
        if (!confirm('Remove this weight variant? Its flavours will also be removed.')) return;
        await persistVariants(variants.filter(v => v.id !== id));
    };
    const addVar = async () => {
        if (!newVar.size_label.trim()) return;
        await persistVariants([...variants, { ...newVar, id: cryptoRandom(), price: parseFloat(newVar.price) || 0, moq: parseInt(newVar.moq) || 1, active: true, sort_order: variants.length + 1 }]);
        setNewVar({ size_label: '', moq: 1, price: 0 }); setAddingVariant(false);
    };
    const updateVarField = async (id, field, val) => {
        // Coerce only numeric fields. Everything else (face_image_url, size_label,
        // active, etc.) must pass through as-is — otherwise a URL string like
        // "https://res.cloudinary.com/..." would be turned into NaN by parseFloat
        // and the face image would never actually save.
        let coerced = val;
        if (field === 'moq') coerced = parseInt(val || '1', 10);
        else if (field === 'price') coerced = parseFloat(val || '0');
        const next = variants.map(v => v.id === id ? { ...v, [field]: coerced } : v);
        await persistVariants(next);
    };

    return (
        <div className="space-y-4">
            {variants.sort((a,b) => (a.sort_order||0) - (b.sort_order||0)).map((v, i) => (
                <WeightGroupCard
                    key={v.id}
                    realm={realm}
                    product={product}
                    variant={v}
                    categoryName={categoryName}
                    allFlavours={flavoursList.data}
                    reloadFlavours={flavoursList.reload}
                    onMoveUp={() => moveVar(i, -1)}
                    onMoveDown={() => moveVar(i, 1)}
                    onRemove={() => removeVar(v.id)}
                    onUpdateField={(field, val) => updateVarField(v.id, field, val)}
                />
            ))}
            {addingVariant ? (
                <div className="card-soft p-3">
                    <div className="text-sdf-red font-bold text-xs tracking-widest mb-2">ADD WEIGHT VARIANT</div>
                    <div className="grid grid-cols-3 gap-2">
                        <Input label="Size Label (e.g. 200g)" value={newVar.size_label} onChange={(v) => setNewVar({ ...newVar, size_label: v })} tid="new-var-size" />
                        <Input label="MOQ" type="number" step="1" min="1" value={newVar.moq} onChange={(v) => setNewVar({ ...newVar, moq: v })} tid="new-var-moq" />
                        <Input label="Base Price" type="number" value={newVar.price} onChange={(v) => setNewVar({ ...newVar, price: v })} tid="new-var-price" />
                    </div>
                    <div className="mt-3 flex gap-2">
                        <button onClick={addVar} className="btn-red" data-testid="new-var-save"><Check size={14} /> Save</button>
                        <button onClick={() => setAddingVariant(false)} className="btn-outline">Cancel</button>
                    </div>
                </div>
            ) : (
                <button onClick={() => setAddingVariant(true)} className="btn-outline" data-testid="add-variant"><Plus size={14} /> Add Weight Variant</button>
            )}
        </div>
    );
}

function cryptoRandom() { return (globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)) ; }

function WeightGroupCard({ realm, product, variant, categoryName, allFlavours, reloadFlavours, onMoveUp, onMoveDown, onRemove, onUpdateField }) {
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState('');
    const [editing, setEditing] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [movingId, setMovingId] = useState(null);

    // Derive this variant's flavours from the parent's single source of
    // truth. NO local `flavours` state, NO per-card GET, NO useEffect
    // race — identical to how Hero Slides derives its list from useList.
    const flavours = useMemo(
        () => (allFlavours || [])
            .filter(f => f.variant_id === variant.id)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
        [allFlavours, variant.id]
    );

    const addFlavour = async () => {
        if (!name.trim()) return;
        await authed(realm, { method: 'POST', url: `/admin/products/${product.id}/flavours`, data: { name: name.trim(), variant_id: variant.id, price: variant.price || 0, sort_order: flavours.length + 1 } });
        setName(''); setAdding(false); reloadFlavours();
    };
    const del = async (id) => {
        if (!confirm('Delete this flavour?')) return;
        await authed(realm, { method: 'DELETE', url: `/admin/flavours/${id}` });
        reloadFlavours();
    };
    // Reorder now uses the EXACT same pattern as Hero Slides:
    //   POST /reorder → await reloadFlavours() → server-authoritative render.
    // No optimistic UI, no local state, no reconciliation logic.
    const move = async (idx, delta) => {
        const cur = flavours[idx];
        if (!cur) return;
        if (movingId) return;
        if (delta < 0 && idx === 0) return;
        if (delta > 0 && idx === flavours.length - 1) return;
        const direction = delta < 0 ? 'up' : 'down';
        setMovingId(cur.id);
        try {
            await authed(realm, {
                method: 'POST',
                url: `/admin/flavours/${cur.id}/reorder?direction=${direction}`,
            });
            await reloadFlavours();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[FLAV-REORDER] failed', err);
            alert('Reorder failed. Please try again.');
        } finally {
            setMovingId(null);
        }
    };
    const saveEdit = async () => {
        await authed(realm, { method: 'PUT', url: `/admin/flavours/${editing.id}`, data: editing });
        setEditing(null); reloadFlavours();
    };

    const setImageUrl = async (flavour, imageUrl) => {
        await authed(realm, { method: 'PUT', url: `/admin/flavours/${flavour.id}`, data: { ...flavour, image_url: imageUrl } });
        reloadFlavours();
    };

    return (
        <div className="card-soft p-4">
            {/* Header row — Batch B2 Item 4 fix: responsive layout, no horizontal overflow.
                Two logical groups:
                  · Left: reorder arrows + title/summary (flex-1, min-width:0 so it can shrink & truncate).
                  · Right: MOQ / price / actions in a wrap-friendly flex-wrap container so they
                    never push the card wider than its parent. */}
            <div className="flex flex-wrap items-start gap-3">
                <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={onMoveUp} className="p-1 hover:bg-neutral-100 rounded" aria-label="Up"><ChevronUp size={16} /></button>
                    <button onClick={onMoveDown} className="p-1 hover:bg-neutral-100 rounded" aria-label="Down"><ChevronDown size={16} /></button>
                </div>
                <div className="min-w-0 flex-1 basis-40">
                    <div className="font-extrabold text-lg text-sdf-text truncate">{variant.size_label} {categoryName}</div>
                    <div className="text-xs text-sdf-text-2 truncate">{flavours.length} flavour{flavours.length === 1 ? '' : 's'} · MOQ {variant.moq} · Base {formatMoney(variant.price)}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0 w-full sm:w-auto justify-start sm:justify-end">
                    <label className="text-[10px] font-bold text-sdf-text-2 tracking-widest inline-flex items-center gap-1">MOQ
                        <input type="number" min="1" step="1" value={variant.moq} onChange={(e) => onUpdateField('moq', e.target.value)} className="w-16 border border-sdf-line rounded-md px-2 py-1 text-xs" />
                    </label>
                    <label className="text-[10px] font-bold text-sdf-text-2 tracking-widest inline-flex items-center gap-1">₹
                        <input type="number" min="0" step="0.01" inputMode="decimal" value={variant.price} onChange={(e) => onUpdateField('price', e.target.value)} className="w-20 border border-sdf-line rounded-md px-2 py-1 text-xs" />
                    </label>
                    <button onClick={() => setAdding(true)} className="btn-red text-xs" data-testid={`add-flavour-${variant.size_label}`}><Plus size={12} /> ADD FLAVOUR</button>
                    <button onClick={() => setCollapsed(!collapsed)} className="btn-outline text-xs" data-testid={`collapse-variant-${variant.size_label}`}>{collapsed ? 'Expand' : 'Collapse'}</button>
                    <button onClick={onRemove} className="p-2 text-red-600 hover:bg-red-50 rounded-md" title="Remove variant" data-testid={`remove-variant-${variant.size_label}`}><Trash2 size={16} /></button>
                </div>
            </div>
            {!collapsed && (
                <>
                    {/* Face Image control (Batch B2 Bug-Fix Pass 3, Feature 2).
                        This image is what distributors see on the product-listing
                        card for this size/weight variant. If unset (empty), the
                        card falls back automatically to the first flavour's image
                        so existing setups keep working with zero migration. */}
                    <FaceImageControl
                        realm={realm}
                        variant={variant}
                        firstFlavourImage={flavours[0]?.image_url || ''}
                        onSet={(newUrl) => onUpdateField('face_image_url', newUrl)}
                        onClear={() => onUpdateField('face_image_url', '')}
                    />
                    {adding && (
                        <div className="mt-3 p-3 rounded-xl bg-sdf-cream/60 flex items-center gap-2">
                            <input placeholder="Flavour name" value={name} onChange={(e) => setName(e.target.value)} className="flex-1 border border-sdf-line rounded-xl px-3 py-2 text-sm bg-white" data-testid={`new-flavour-name-${variant.size_label}`} />
                            <button onClick={addFlavour} className="btn-red" data-testid={`new-flavour-save-${variant.size_label}`}><Check size={14} /> Save</button>
                            <button onClick={() => { setName(''); setAdding(false); }} className="btn-outline">Cancel</button>
                        </div>
                    )}
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                        {flavours.map((f, i) => (
                            <div key={f.id} className="rounded-2xl border border-sdf-line bg-white overflow-hidden">
                                <div className="relative aspect-square bg-neutral-100">
                                    {f.image_url ? <img src={absUrl(f.image_url)} alt={f.name} className="w-full h-full object-cover" /> : (
                                        <div className="w-full h-full flex items-center justify-center text-xs text-neutral-400 p-3 text-center">{f.name}</div>
                                    )}
                                    <div className="absolute top-2 right-2">
                                        <ImagePickerButton
                                            realm={realm}
                                            subdir="flavours"
                                            label={f.image_url ? 'Change' : 'Image'}
                                            size="sm"
                                            variant="outline"
                                            onPicked={(url) => setImageUrl(f, url)}
                                            testIdPrefix={`flavour-img-${f.id}`}
                                        />
                                    </div>
                                </div>
                                <div className="p-2">
                                    {editing?.id === f.id ? (
                                        <div className="space-y-1">
                                            <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full border border-sdf-line rounded-md px-2 py-1 text-xs" />
                                            <input type="number" min="0" step="0.01" inputMode="decimal" value={editing.price} onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value) || 0 })} className="w-full border border-sdf-line rounded-md px-2 py-1 text-xs" />
                                            <textarea
                                                value={editing.description || ''}
                                                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                                placeholder="Description (optional)"
                                                rows={2}
                                                className="w-full border border-sdf-line rounded-md px-2 py-1 text-[11px] resize-none"
                                                data-testid={`flavour-desc-${f.id}`}
                                            />
                                            <div className="flex gap-1">
                                                <button onClick={saveEdit} className="btn-red flex-1 py-1 px-2 text-xs">Save</button>
                                                <button onClick={() => setEditing(null)} className="btn-outline flex-1 py-1 px-2 text-xs">X</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="text-sm font-bold text-sdf-text line-clamp-1">{f.name}</div>
                                            <div className="text-xs text-sdf-red font-bold">{formatMoney(f.price)}</div>
                                            {/* Batch B2 Bug-Fix Pass 6 — enlarge Up/Down tap targets on
                                                mobile. Previously the buttons were ~20×20 px which is well
                                                below the 44×44 minimum recommended touch target: users on
                                                phones reported the reorder "not working" because they were
                                                mis-tapping. `min-w-[36px] min-h-[36px]` + `touch-manipulation`
                                                (eliminates 300 ms tap delay) + explicit `type="button"`
                                                (avoids accidental form-submit) makes reorder reliable on
                                                every device. Visual size on desktop is unchanged thanks
                                                to the same p-1 padding + small icon. */}
                                            <div className="mt-1.5 flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => move(i, -1)}
                                                    disabled={i === 0 || movingId === f.id || !!movingId}
                                                    aria-label="Move up"
                                                    data-testid={`flavour-up-${f.id}`}
                                                    className="min-w-[36px] min-h-[36px] flex items-center justify-center rounded bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300 disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation select-none"
                                                >
                                                    <ChevronUp size={16} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => move(i, 1)}
                                                    disabled={i === flavours.length - 1 || movingId === f.id || !!movingId}
                                                    aria-label="Move down"
                                                    data-testid={`flavour-down-${f.id}`}
                                                    className="min-w-[36px] min-h-[36px] flex items-center justify-center rounded bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300 disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation select-none"
                                                >
                                                    <ChevronDown size={16} />
                                                </button>
                                                <button type="button" onClick={() => setEditing({ ...f })} className="ml-auto text-[10px] font-bold px-2 min-h-[36px] rounded-full bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300 touch-manipulation">Edit</button>
                                                <button type="button" onClick={() => del(f.id)} aria-label="Delete flavour" className="min-w-[36px] min-h-[36px] flex items-center justify-center text-red-600 hover:bg-red-50 rounded touch-manipulation"><Trash2 size={14} /></button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    {flavours.length === 0 && !adding && <div className="mt-2 text-xs text-sdf-text-2">No flavours yet. Click ADD FLAVOUR to add the first one.</div>}
                </>
            )}
        </div>
    );
}

function ProductEditor({ realm, product, onClose }) {
    const [variants, setVariants] = useState(product.variants || []);
    const [flavours, setFlavours] = useState([]);
    const [activeVar, setActiveVar] = useState(product.variants?.[0]?.id || null);

    const loadFlavours = useCallback(async () => {
        const r = await authed(realm, { method: 'GET', url: `/admin/products/${product.id}/flavours` });
        setFlavours(r.data);
    }, [realm, product.id]);

    useEffect(() => { loadFlavours(); }, [loadFlavours]);

    const updatePrice = async (vId, field, val) => {
        const next = variants.map(v => v.id === vId ? { ...v, [field]: field === 'moq' ? parseInt(val || '1', 10) : parseFloat(val || '0') } : v);
        setVariants(next);
    };
    const saveVariants = async () => {
        await authed(realm, { method: 'PUT', url: `/admin/products/${product.id}`, data: { ...product, variants } });
        onClose();
    };

    const flavoursForVar = flavours.filter(f => f.variant_id === activeVar);

    const editFlavourPrice = async (fid, price) => {
        await authed(realm, { method: 'PUT', url: `/admin/flavours/${fid}`, data: { name: flavours.find(f => f.id === fid).name, variant_id: activeVar, price: parseFloat(price || '0') } });
        loadFlavours();
    };
    const [newFlavour, setNewFlavour] = useState('');
    const addFlavour = async () => {
        if (!newFlavour.trim() || !activeVar) return;
        const variant = variants.find(v => v.id === activeVar);
        await authed(realm, { method: 'POST', url: `/admin/products/${product.id}/flavours`, data: { name: newFlavour.trim(), variant_id: activeVar, price: variant?.price || 0 } });
        setNewFlavour(''); loadFlavours();
    };
    const delFlavour = async (fid) => { await authed(realm, { method: 'DELETE', url: `/admin/flavours/${fid}` }); loadFlavours(); };

    return (
        <div className="mt-3 p-3 bg-sdf-cream/60 rounded-xl">
            <div className="font-bold text-sm">Variants (weight / MOQ / base price)</div>
            <div className="mt-2 space-y-2">
                {variants.map(v => (
                    <div key={v.id} className="grid grid-cols-4 gap-2 items-center">
                        <div className="text-sm font-semibold">{v.size_label}</div>
                        <Input label="MOQ" type="number" step="1" min="1" value={v.moq} onChange={(x) => updatePrice(v.id, 'moq', x)} />
                        <Input label="Base Price" type="number" value={v.price} onChange={(x) => updatePrice(v.id, 'price', x)} />
                        <button onClick={() => setActiveVar(v.id)} className={`btn-outline ${activeVar === v.id ? 'ring-2 ring-sdf-red' : ''}`}>Manage flavours</button>
                    </div>
                ))}
            </div>
            <div className="mt-3 flex gap-2">
                <button onClick={saveVariants} className="btn-red"><Check size={14} /> Save Variants</button>
                <button onClick={onClose} className="btn-outline">Close</button>
            </div>
            {activeVar && (
                <div className="mt-4 border-t border-sdf-line pt-3">
                    <div className="font-bold text-sm">Flavours for {variants.find(v => v.id === activeVar)?.size_label}</div>
                    <div className="mt-2 flex items-center gap-2">
                        <input placeholder="New flavour name" value={newFlavour} onChange={(e) => setNewFlavour(e.target.value)} className="flex-1 border border-sdf-line rounded-xl px-3 py-2 text-sm bg-white" data-testid="flavour-new" />
                        <button onClick={addFlavour} className="btn-red" data-testid="flavour-add">Add</button>
                    </div>
                    <div className="mt-2 space-y-1">
                        {flavoursForVar.map(f => (
                            <div key={f.id} className="flex items-center gap-2 bg-white rounded-lg p-2 border border-sdf-line">
                                <div className="flex-1 text-sm">{f.name}</div>
                                <input type="number" min="0" step="0.01" inputMode="decimal" defaultValue={f.price} onBlur={(e) => editFlavourPrice(f.id, e.target.value)} className="w-24 border border-sdf-line rounded-md px-2 py-1 text-sm" />
                                <button onClick={() => delFlavour(f.id)} className="p-1 text-red-600"><Trash2 size={14} /></button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function CityPricingTab({ realm }) {
    const { data, loading, reload } = useList(realm, '/admin/city-pricing');
    const [city, setCity] = useState('');
    const [flavourId, setFlavourId] = useState('');
    const [price, setPrice] = useState('');
    const [productSearch, setProductSearch] = useState('');
    const [products, setProducts] = useState([]);

    useEffect(() => { (async () => { const r = await authed(realm, { method: 'GET', url: '/admin/products' }); setProducts(r.data); })(); }, [realm]);
    const allFlavours = useMemo(() => {
        const out = [];
        products.forEach(p => (p.variants || []).forEach(v => {}));
        // Requires loading flavours; use a lazy load here per selection. For simplicity, we use text-input flavour_id.
        return out;
    }, [products]);

    const save = async () => {
        if (!city || !flavourId || !price) return;
        await authed(realm, { method: 'POST', url: '/admin/city-pricing', data: { city, flavour_id: flavourId, price: parseFloat(price) } });
        setCity(''); setFlavourId(''); setPrice(''); reload();
    };
    const del = async (id) => { await authed(realm, { method: 'DELETE', url: `/admin/city-pricing/${id}` }); reload(); };

    return (
        <Section title="City-wise Pricing Overrides">
            <div className="card-soft p-3 mb-3">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                    <Input label="City" value={city} onChange={setCity} />
                    <Input label="Flavour ID" value={flavourId} onChange={setFlavourId} />
                    <Input label="Price" type="number" value={price} onChange={setPrice} />
                    <button onClick={save} className="btn-red" data-testid="city-save">Save Override</button>
                </div>
                <div className="mt-2 text-xs text-sdf-text-2">Tip: Flavour IDs are visible on the products endpoint. Use Products tab → Edit → flavours list to copy an ID (future UI will allow picking directly).</div>
            </div>
            {loading ? <div className="spinner"/> : (
                <div className="space-y-1">
                    {data.map(c => (
                        <div key={c.id || (c.city_lc + c.flavour_id)} className="card-soft p-2 flex items-center gap-3 text-sm">
                            <div className="font-bold w-32">{c.city}</div>
                            <div className="flex-1 truncate text-xs text-sdf-text-2">{c.flavour_id}</div>
                            <div className="font-bold">₹{c.price}</div>
                            <button onClick={() => del(c.id)} className="p-1 text-red-600"><Trash2 size={14}/></button>
                        </div>
                    ))}
                    {data.length === 0 && <div className="text-sm text-sdf-text-2">No overrides configured.</div>}
                </div>
            )}
        </Section>
    );
}

function DistributorsTab({ realm, mode }) {
    const isSuperAdmin = mode === 'super_admin';
    const [tab, setTab] = useState('pending');
    const [showAdd, setShowAdd] = useState(false);
    const [addForm, setAddForm] = useState({ name: '', mobile: '', company_name: '', city: '' });
    const [addBusy, setAddBusy] = useState(false);
    const [addErr, setAddErr] = useState('');
    // Edit Distributor (Super Admin only) — Feature 1
    const [editing, setEditing] = useState(null); // holds the distributor being edited, or null
    const [editForm, setEditForm] = useState({ name: '', mobile: '', company_name: '', city: '' });
    const [editBusy, setEditBusy] = useState(false);
    const [editErr, setEditErr] = useState('');
    // Unsuspend confirmation (Super Admin only) — Feature 2
    const [unsuspendTarget, setUnsuspendTarget] = useState(null); // distributor pending confirmation
    const [unsuspendBusy, setUnsuspendBusy] = useState(false);
    // Delete Distributor (Super Admin only) — TOTP-gated
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteCode, setDeleteCode] = useState('');
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteErr, setDeleteErr] = useState('');
    const { data, loading, reload } = useList(realm, `/admin/distributors?status=${tab}`, [tab]);
    const approve = async (id) => { await authed(realm, { method: 'POST', url: `/admin/distributors/${id}/approve` }); reload(); };
    const reject = async (id) => { await authed(realm, { method: 'POST', url: `/admin/distributors/${id}/reject` }); reload(); };
    const suspend = async (id) => { await authed(realm, { method: 'POST', url: `/admin/distributors/${id}/suspend` }); reload(); };

    const submitAdd = async () => {
        setAddErr('');
        setAddBusy(true);
        try {
            await authed(realm, { method: 'POST', url: '/admin/distributors/manual', data: addForm });
            setShowAdd(false);
            setAddForm({ name: '', mobile: '', company_name: '', city: '' });
            setTab('approved');
            reload();
        } catch (e) {
            setAddErr(e.response?.data?.detail || 'Failed to add distributor.');
        } finally { setAddBusy(false); }
    };

    // Feature 1 — open Edit dialog pre-filled with the distributor's current
    // values. We treat person_name as the canonical "Distributor Name" that
    // appears in the Add flow (which populates both person_name and
    // proprietor_name together — matched by the backend edit endpoint).
    const openEdit = (d) => {
        setEditErr('');
        setEditing(d);
        setEditForm({
            name: d.person_name || '',
            mobile: d.mobile || '',
            company_name: d.business_name || '',
            city: d.city || '',
        });
    };
    const closeEdit = () => { setEditing(null); setEditErr(''); setEditBusy(false); };
    const submitEdit = async () => {
        if (!editing) return;
        setEditErr('');
        setEditBusy(true);
        try {
            await authed(realm, { method: 'PUT', url: `/admin/distributors/${editing.id}`, data: editForm });
            closeEdit();
            reload();
        } catch (e) {
            setEditErr(e.response?.data?.detail || 'Failed to update distributor.');
        } finally { setEditBusy(false); }
    };

    // Feature 2 — Unsuspend flow with explicit confirmation.
    const confirmUnsuspend = async () => {
        if (!unsuspendTarget) return;
        setUnsuspendBusy(true);
        try {
            await authed(realm, { method: 'POST', url: `/admin/distributors/${unsuspendTarget.id}/unsuspend` });
            const name = unsuspendTarget.person_name || unsuspendTarget.business_name || 'Distributor';
            setUnsuspendTarget(null);
            reload();
            alert(`${name} has been unsuspended and moved to Approved.`);
        } catch (e) {
            alert(e.response?.data?.detail || 'Failed to unsuspend distributor.');
        } finally { setUnsuspendBusy(false); }
    };

    // Delete distributor flow — opens TOTP prompt, verifies via backend.
    const openDelete = (d) => { setDeleteTarget(d); setDeleteCode(''); setDeleteErr(''); };
    const closeDelete = () => { if (!deleteBusy) { setDeleteTarget(null); setDeleteCode(''); setDeleteErr(''); } };
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const code = (deleteCode || '').replace(/\D/g, '').slice(0, 6);
        if (code.length !== 6) { setDeleteErr('Enter the 6-digit Google Authenticator code.'); return; }
        setDeleteErr('');
        setDeleteBusy(true);
        try {
            await authed(realm, {
                method: 'POST',
                url: `/admin/distributors/${deleteTarget.id}/delete`,
                data: { totp_code: code },
            });
            const name = deleteTarget.business_name || deleteTarget.person_name || 'Distributor';
            setDeleteTarget(null); setDeleteCode(''); setDeleteErr('');
            reload();
            alert(`${name} has been deleted.`);
        } catch (e) {
            setDeleteErr(e.response?.data?.detail || 'Failed to delete distributor.');
        } finally { setDeleteBusy(false); }
    };

    return (
        <Section title="Distributors">
            <div className="flex flex-wrap items-center gap-2 mb-3">
                {['pending', 'approved', 'rejected', 'suspended'].map(s => (
                    <button key={s} onClick={() => setTab(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${tab === s ? 'bg-sdf-red text-white border-sdf-red' : 'bg-white border-sdf-line'}`}>{s.toUpperCase()}</button>
                ))}
                <div className="flex-1" />
                <button onClick={() => setShowAdd(true)} className="btn-red" data-testid="distributors-add-btn">
                    <Plus size={14} /> Add Distributor
                </button>
            </div>
            {showAdd && (
                <div className="fixed inset-0 z-50 animate-fade-in flex items-center justify-center" onClick={() => setShowAdd(false)} data-testid="distributor-add-modal">
                    <div className="absolute inset-0 bg-black/50" />
                    <div className="relative w-full max-w-md mx-4 bg-sdf-ivory rounded-2xl shadow-popup animate-slide-up overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="px-4 py-3 border-b border-sdf-line flex items-center justify-between">
                            <div className="text-sm font-extrabold text-sdf-text tracking-widest">ADD DISTRIBUTOR</div>
                            <button onClick={() => setShowAdd(false)} className="p-1.5 rounded-full hover:bg-black/5" aria-label="Close"><X size={16} /></button>
                        </div>
                        <div className="p-4 space-y-3">
                            <Input label="Name" value={addForm.name} onChange={(v) => setAddForm({ ...addForm, name: v })} tid="add-dist-name" />
                            <Input label="Phone Number" value={addForm.mobile} onChange={(v) => setAddForm({ ...addForm, mobile: v.replace(/\D/g, '').slice(0, 10) })} tid="add-dist-mobile" />
                            <Input label="Company Name" value={addForm.company_name} onChange={(v) => setAddForm({ ...addForm, company_name: v })} tid="add-dist-company" />
                            <Input label="City" value={addForm.city} onChange={(v) => setAddForm({ ...addForm, city: v })} tid="add-dist-city" />
                            <p className="text-[11px] text-sdf-text-2">All fields are optional. Provide at least one so the record is not empty.</p>
                            {addErr && <div className="text-xs text-red-700" data-testid="add-dist-error">{addErr}</div>}
                        </div>
                        <div className="border-t border-sdf-line px-4 py-3 flex items-center justify-end gap-2">
                            <button onClick={() => setShowAdd(false)} className="btn-outline">Cancel</button>
                            <button onClick={submitAdd} disabled={addBusy} className="btn-red" data-testid="add-dist-save">{addBusy ? 'Saving…' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Feature 1 — Edit Distributor dialog (Super Admin only). Mirrors
                the Add Distributor dialog exactly so behaviour is identical. */}
            {isSuperAdmin && editing && (
                <div className="fixed inset-0 z-50 animate-fade-in flex items-center justify-center" onClick={closeEdit} data-testid="distributor-edit-modal">
                    <div className="absolute inset-0 bg-black/50" />
                    <div className="relative w-full max-w-md mx-4 bg-sdf-ivory rounded-2xl shadow-popup animate-slide-up overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="px-4 py-3 border-b border-sdf-line flex items-center justify-between">
                            <div className="text-sm font-extrabold text-sdf-text tracking-widest">EDIT DISTRIBUTOR</div>
                            <button onClick={closeEdit} className="p-1.5 rounded-full hover:bg-black/5" aria-label="Close"><X size={16} /></button>
                        </div>
                        <div className="p-4 space-y-3">
                            <Input label="Name" value={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} tid="edit-dist-name" />
                            <Input label="Phone Number" value={editForm.mobile} onChange={(v) => setEditForm({ ...editForm, mobile: v.replace(/\D/g, '').slice(0, 10) })} tid="edit-dist-mobile" />
                            <Input label="Company Name" value={editForm.company_name} onChange={(v) => setEditForm({ ...editForm, company_name: v })} tid="edit-dist-company" />
                            <Input label="City" value={editForm.city} onChange={(v) => setEditForm({ ...editForm, city: v })} tid="edit-dist-city" />
                            {editErr && <div className="text-xs text-red-700" data-testid="edit-dist-error">{editErr}</div>}
                        </div>
                        <div className="border-t border-sdf-line px-4 py-3 flex items-center justify-end gap-2">
                            <button onClick={closeEdit} className="btn-outline" data-testid="edit-dist-cancel">Cancel</button>
                            <button onClick={submitEdit} disabled={editBusy} className="btn-red" data-testid="edit-dist-save">{editBusy ? 'Saving…' : 'Save Changes'}</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Delete Distributor TOTP dialog (Super Admin only). */}
            {isSuperAdmin && deleteTarget && (
                <div className="fixed inset-0 z-50 animate-fade-in flex items-center justify-center" onClick={closeDelete} data-testid="distributor-delete-modal">
                    <div className="absolute inset-0 bg-black/50" />
                    <div className="relative w-full max-w-sm mx-4 bg-sdf-ivory rounded-2xl shadow-popup animate-slide-up overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="px-4 py-3 border-b border-sdf-line">
                            <div className="text-sm font-extrabold text-red-700 tracking-widest">DELETE DISTRIBUTOR</div>
                        </div>
                        <div className="p-4 space-y-3">
                            <p className="text-sm text-sdf-text">
                                This will permanently delete the distributor record. This action cannot be undone.
                            </p>
                            <p className="text-xs text-sdf-text-2">
                                <span className="font-semibold">{deleteTarget.business_name || deleteTarget.person_name || 'Distributor'}</span>
                                {deleteTarget.mobile ? ` · +91 ${deleteTarget.mobile}` : ''}
                            </p>
                            <p className="text-xs text-sdf-text-2">
                                Enter the current 6-digit code from your Google Authenticator app to confirm.
                            </p>
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                placeholder="6-digit code"
                                value={deleteCode}
                                onChange={(e) => setDeleteCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                className="w-full border border-sdf-line rounded-xl px-3 py-2 text-lg tracking-[0.4em] font-mono bg-white text-center"
                                data-testid="delete-dist-totp-input"
                                autoFocus
                            />
                            {deleteErr && <div className="text-xs text-red-700" data-testid="delete-dist-error">{deleteErr}</div>}
                        </div>
                        <div className="border-t border-sdf-line px-4 py-3 flex items-center justify-end gap-2">
                            <button onClick={closeDelete} disabled={deleteBusy} className="btn-outline" data-testid="delete-dist-cancel">Cancel</button>
                            <button
                                onClick={confirmDelete}
                                disabled={deleteBusy || deleteCode.length !== 6}
                                className="inline-flex items-center gap-1.5 rounded-full bg-red-700 text-white font-bold px-4 py-2 text-sm hover:bg-red-800 disabled:opacity-60"
                                data-testid="delete-dist-confirm"
                            >
                                {deleteBusy ? 'Deleting…' : 'Delete Distributor'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Feature 2 — Unsuspend confirmation (Super Admin only). */}
            {isSuperAdmin && unsuspendTarget && (
                <div className="fixed inset-0 z-50 animate-fade-in flex items-center justify-center" onClick={() => !unsuspendBusy && setUnsuspendTarget(null)} data-testid="distributor-unsuspend-modal">
                    <div className="absolute inset-0 bg-black/50" />
                    <div className="relative w-full max-w-sm mx-4 bg-sdf-ivory rounded-2xl shadow-popup animate-slide-up overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="px-4 py-3 border-b border-sdf-line">
                            <div className="text-sm font-extrabold text-sdf-text tracking-widest">UNSUSPEND DISTRIBUTOR</div>
                        </div>
                        <div className="p-4">
                            <p className="text-sm text-sdf-text">Are you sure you want to unsuspend this distributor?</p>
                            <p className="text-xs text-sdf-text-2 mt-2">
                                <span className="font-semibold">{unsuspendTarget.business_name || unsuspendTarget.person_name || 'Distributor'}</span>
                                {unsuspendTarget.mobile ? ` · +91 ${unsuspendTarget.mobile}` : ''}
                            </p>
                        </div>
                        <div className="border-t border-sdf-line px-4 py-3 flex items-center justify-end gap-2">
                            <button onClick={() => setUnsuspendTarget(null)} disabled={unsuspendBusy} className="btn-outline" data-testid="unsuspend-no">No</button>
                            <button onClick={confirmUnsuspend} disabled={unsuspendBusy} className="btn-red" data-testid="unsuspend-yes">{unsuspendBusy ? 'Working…' : 'Yes'}</button>
                        </div>
                    </div>
                </div>
            )}
            {loading ? <div className="spinner"/> :
                data.length === 0 ? <div className="text-sm text-sdf-text-2">No distributors in this status.</div> :
                    <div className="space-y-2">
                        {data.map(d => (
                            <div key={d.id} className="card-soft p-3">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="font-bold">{d.business_name} <span className="text-xs text-sdf-text-2">({d.city}, {d.state})</span></div>
                                        <div className="text-xs text-sdf-text-2">{d.person_name} · Proprietor {d.proprietor_name} · +91 {d.mobile}</div>
                                        <div className="text-xs text-sdf-text-2">{d.address_line} · {d.pin_code}</div>
                                        {(d.gstin || d.pan || d.email) && <div className="text-xs text-sdf-text-2">GST: {d.gstin || '—'} · PAN: {d.pan || '—'} · {d.email || '—'}</div>}
                                    </div>
                                    <div className="flex flex-col gap-2 items-end">
                                        {/* Feature 1 — pencil Edit icon, super-admin only, shown on every card in every tab. */}
                                        {isSuperAdmin && (
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => openEdit(d)}
                                                    title="Edit distributor"
                                                    aria-label="Edit distributor"
                                                    className="p-1.5 rounded-full border border-sdf-line bg-white hover:bg-neutral-50 text-sdf-text"
                                                    data-testid={`edit-dist-${d.mobile || d.id}`}
                                                >
                                                    <Edit3 size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => openDelete(d)}
                                                    title="Delete distributor"
                                                    aria-label="Delete distributor"
                                                    className="p-1.5 rounded-full border border-red-200 bg-white hover:bg-red-50 text-red-700"
                                                    data-testid={`delete-dist-${d.mobile || d.id}`}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        )}
                                        {tab === 'pending' && (
                                            <>
                                                <button onClick={() => approve(d.id)} className="btn-red" data-testid={`approve-${d.mobile}`}><Check size={14}/> Approve</button>
                                                <button onClick={() => reject(d.id)} className="btn-outline" data-testid={`reject-${d.mobile}`}><X size={14}/> Reject</button>
                                            </>
                                        )}
                                        {tab === 'approved' && (
                                            <button onClick={() => suspend(d.id)} className="btn-outline">Suspend</button>
                                        )}
                                        {tab === 'rejected' && (
                                            <button onClick={() => approve(d.id)} className="btn-red">Approve Now</button>
                                        )}
                                        {/* Feature 2 — Unsuspend button, super-admin only, only in the suspended tab. */}
                                        {tab === 'suspended' && isSuperAdmin && (
                                            <button
                                                onClick={() => setUnsuspendTarget(d)}
                                                className="btn-red"
                                                data-testid={`unsuspend-${d.mobile || d.id}`}
                                            >
                                                <RotateCcw size={14}/> Unsuspend
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
            }
        </Section>
    );
}

function OrdersTab({ realm }) {
    const [status, setStatus] = useState('');
    const [payment, setPayment] = useState('');
    const query = (() => {
        const p = new URLSearchParams();
        if (status) p.set('status', status);
        if (payment) p.set('payment_status', payment);
        const s = p.toString();
        return s ? `?${s}` : '';
    })();
    const { data, loading, reload } = useList(realm, `/admin/orders${query}`, [status, payment]);
    const [detailId, setDetailId] = useState(null);
    const canEdit = realm === 'super_admin';
    // Business set going forward. Legacy statuses (packed/shipped) still appear
    // in the ALL filter and read correctly, but are not offered as filters.
    const filters = ['', 'placed', 'confirmed', 'dispatched', 'delivered', 'cancelled'];
    const paymentFilters = ['', 'pending', 'paid'];
    return (
        <Section title="Orders">
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-sdf-text-2 mr-1">Status</span>
                {filters.map(s => (
                    <button key={s || 'all'} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${status === s ? 'bg-sdf-red text-white border-sdf-red' : 'bg-white border-sdf-line hover:bg-neutral-50'}`} data-testid={`admin-orders-filter-${s || 'all'}`}>{s ? s.toUpperCase() : 'ALL'}</button>
                ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-[10px] font-bold uppercase tracking-widest text-sdf-text-2 mr-1">Payment</span>
                {paymentFilters.map(p => (
                    <button
                        key={p || 'all'}
                        onClick={() => setPayment(p)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                            payment === p
                                ? (p === 'pending' ? 'bg-amber-500 text-white border-amber-500' : p === 'paid' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-sdf-text text-white border-sdf-text')
                                : 'bg-white border-sdf-line hover:bg-neutral-50'
                        }`}
                        data-testid={`admin-orders-payment-filter-${p || 'all'}`}
                    >
                        {p ? p.toUpperCase() : 'ALL'}
                    </button>
                ))}
            </div>
            {loading ? <div className="spinner"/> : (
                <div className="overflow-x-auto card-soft">
                    <table className="min-w-full text-sm">
                        <thead className="bg-white/60 text-[10px] font-bold uppercase tracking-widest text-sdf-text-2">
                            <tr>
                                <th className="px-3 py-2 text-left">Order #</th>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-left">City</th>
                                <th className="px-3 py-2 text-left">Payment</th>
                                <th className="px-3 py-2 text-center">Items</th>
                                <th className="px-3 py-2 text-right">Total</th>
                                <th className="px-3 py-2 text-center">Status</th>
                                <th className="px-3 py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(o => {
                                const overdue = isOverdueCollection(o);
                                const age = overdue ? formatOverdueAge(o) : null;
                                return (
                                <tr
                                    key={o.id}
                                    className={`border-t border-sdf-line hover:bg-sdf-cream/30 transition-colors ${overdue ? 'bg-amber-50/70 border-l-4 border-l-amber-500' : ''}`}
                                    data-testid={`admin-order-row-${o.id}`}
                                >
                                    <td className="px-3 py-2 font-mono text-xs">
                                        {overdue ? (
                                            <div className="flex flex-col gap-0.5" data-testid={`admin-order-overdue-${o.id}`}>
                                                <span className="inline-flex items-center gap-1.5" title={`Credit order overdue · ${age}`}>
                                                    <span aria-hidden="true" className="text-amber-600">⚠</span>
                                                    <span>{o.order_number}</span>
                                                </span>
                                                {age && (
                                                    <span
                                                        className="inline-flex items-center px-1.5 py-0 text-[9px] font-bold uppercase tracking-widest rounded-full border bg-amber-100 text-amber-900 border-amber-300 w-fit"
                                                        data-testid={`admin-order-overdue-age-${o.id}`}
                                                    >
                                                        {age} overdue
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            o.order_number
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-sdf-text-2 whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</td>
                                    <td className="px-3 py-2 text-xs">{o.address?.city || o.delivery_address?.city || '—'}</td>
                                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                                        <span className="font-bold">{o.payment_method === 'credit' ? 'CREDIT' : 'COD'}</span>
                                        <span className="mx-1 text-sdf-text-2">·</span>
                                        <PaymentStatusBadge status={o.payment_status} testid={`admin-order-payment-${o.id}`} />
                                    </td>
                                    <td className="px-3 py-2 text-xs text-center">{o.items?.length || 0}</td>
                                    <td className="px-3 py-2 text-right font-extrabold">{formatMoney(o.total)}</td>
                                    <td className="px-3 py-2 text-center">
                                        <OrderStatusBadge status={o.status} testid={`admin-order-status-${o.id}`} />
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button
                                            onClick={() => setDetailId(o.id)}
                                            className="btn-outline text-xs"
                                            data-testid={`admin-order-view-${o.id}`}
                                        >
                                            {canEdit ? 'View / Edit' : 'View'}
                                        </button>
                                    </td>
                                </tr>
                                );
                            })}
                            {data.length === 0 && (
                                <tr><td colSpan={8} className="px-3 py-6 text-center text-sm text-sdf-text-2">No orders in this filter.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            {detailId && (
                <OrderDetailsModal
                    orderId={detailId}
                    realm={realm}
                    canEdit={canEdit}
                    onClose={() => setDetailId(null)}
                    onStatusChanged={() => reload()}
                    onDeleted={() => { setDetailId(null); reload(); }}
                />
            )}
        </Section>
    );
}

function CategoriesTab({ realm }) {
    const { data, loading, reload } = useList(realm, '/admin/categories');
    const [showHidden, setShowHidden] = useState(false);
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState(''); const [slug, setSlug] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});

    const visibleCategories = data.filter(c => showHidden || c.active !== false);

    const add = async () => {
        if (!name || !slug) return;
        await authed(realm, { method: 'POST', url: '/admin/categories', data: { name, slug, sort_order: data.length + 1, active: true } });
        setName(''); setSlug(''); setCreating(false); reload();
    };
    const del = async (id) => { if (!confirm('Delete category?')) return; await authed(realm, { method: 'DELETE', url: `/admin/categories/${id}` }); reload(); };
    const toggleActive = async (c) => { await authed(realm, { method: 'PUT', url: `/admin/categories/${c.id}`, data: { ...c, active: !c.active } }); reload(); };
    const move = async (idx, delta) => {
        const sorted = [...visibleCategories].sort((a,b) => (a.sort_order||0) - (b.sort_order||0));
        const swap = idx + delta;
        if (swap < 0 || swap >= sorted.length) return;
        [sorted[idx], sorted[swap]] = [sorted[swap], sorted[idx]];
        for (let i = 0; i < sorted.length; i++) {
            await authed(realm, { method: 'PUT', url: `/admin/categories/${sorted[i].id}`, data: { ...sorted[i], sort_order: i + 1 } });
        }
        reload();
    };
    const saveEdit = async () => {
        await authed(realm, { method: 'PUT', url: `/admin/categories/${editingId}`, data: editForm });
        setEditingId(null); reload();
    };

    // "Active sizes" count from products data
    const [prods, setProds] = useState([]);
    useEffect(() => { (async () => { const r = await authed(realm, { method: 'GET', url: '/admin/products' }); setProds(r.data); })(); }, [realm, data]);
    const sizesCount = (slug) => {
        const cat = prods.filter(p => p.category_slug === slug);
        return cat.reduce((acc, p) => acc + (p.variants || []).filter(v => v.active !== false).length, 0);
    };

    return (
        <div className="space-y-4">
            {/* Header card matching Retailers style */}
            <div className="card-soft p-5">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                        <div className="w-12 h-12 rounded-2xl bg-red-100 text-sdf-red flex items-center justify-center flex-shrink-0"><Layers size={22} /></div>
                        <div className="flex-1">
                            <h2 className="text-2xl font-extrabold text-sdf-text">Categories &amp; Sizes</h2>
                            <p className="text-xs text-sdf-text-2 mt-1">Reorder, rename, add or hide categories and their per-category sizes. Changes appear across the site immediately.</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 text-xs text-sdf-text-2"><input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} /> Show hidden</label>
                        <button onClick={() => setCreating(true)} className="btn-red" data-testid="new-category-btn"><Plus size={14} /> NEW CATEGORY</button>
                    </div>
                </div>
            </div>

            {creating && (
                <div className="card-soft p-4">
                    <div className="text-sdf-red font-bold text-xs tracking-widest mb-2">NEW CATEGORY</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Input label="Name" value={name} onChange={setName} tid="new-cat-name" />
                        <Input label="Slug" value={slug} onChange={(v) => setSlug(v.toLowerCase().replace(/\s+/g, '-'))} tid="new-cat-slug" />
                        <div className="flex items-end gap-2">
                            <button onClick={add} className="btn-red" data-testid="new-cat-save"><Check size={14} /> Save</button>
                            <button onClick={() => { setCreating(false); setName(''); setSlug(''); }} className="btn-outline">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? <div className="spinner"/> : (
                <div className="space-y-2">
                    {visibleCategories.sort((a,b) => (a.sort_order||0) - (b.sort_order||0)).map((c, i) => (
                        <div key={c.id} className={`card-soft p-3 flex items-center gap-3 ${c.active === false ? 'opacity-60' : ''}`}>
                            <div className="flex flex-col gap-1">
                                <button onClick={() => move(i, -1)} className="p-1 rounded bg-neutral-100 hover:bg-neutral-200"><ChevronUp size={14} /></button>
                                <button onClick={() => move(i, 1)} className="p-1 rounded bg-neutral-100 hover:bg-neutral-200"><ChevronDown size={14} /></button>
                            </div>
                            <div className="flex-1">
                                {editingId === c.id ? (
                                    <div className="grid grid-cols-2 gap-2">
                                        <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="border border-sdf-line rounded-md px-2 py-1 text-sm" />
                                        <input value={editForm.slug} onChange={(e) => setEditForm({ ...editForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} className="border border-sdf-line rounded-md px-2 py-1 text-sm" />
                                    </div>
                                ) : (
                                    <>
                                        <div className="font-bold text-sdf-text">{c.name} <span className="text-xs text-sdf-text-2 font-normal">/{c.slug}</span></div>
                                        <div className="text-xs text-sdf-text-2">{sizesCount(c.slug)} active sizes</div>
                                    </>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {editingId === c.id ? (
                                    <>
                                        <button onClick={saveEdit} className="btn-red text-xs"><Check size={12}/> Save</button>
                                        <button onClick={() => setEditingId(null)} className="btn-outline text-xs">Cancel</button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => alert('Use the Products tab to manage sizes and flavours for this category.')} className="btn-outline text-xs">Manage sizes</button>
                                        <button onClick={() => toggleActive(c)} className="btn-outline text-xs">{c.active === false ? 'Show' : 'Hide'}</button>
                                        <button onClick={() => { setEditingId(c.id); setEditForm({ name: c.name, slug: c.slug, active: c.active, sort_order: c.sort_order }); }} className="p-2 hover:bg-neutral-100 rounded" title="Edit"><Edit3 size={14}/></button>
                                        <button onClick={() => del(c.id)} className="p-2 text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 size={14}/></button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function SettingsTab({ realm, mode }) {
    const [s, setS] = useState(null);
    const [msg, setMsg] = useState('');
    useEffect(() => { (async () => { const r = await authed(realm, { method: 'GET', url: '/admin/settings' }); setS(r.data || {}); })(); }, [realm]);
    const save = async () => {
        const clean = { ...s }; delete clean.id; delete clean._id; delete clean.created_at; delete clean.updated_at;
        if (mode !== 'super_admin') delete clean.gst_number;
        try { await authed(realm, { method: 'PUT', url: '/admin/settings', data: clean }); setMsg('Saved'); setTimeout(() => setMsg(''), 2000); }
        catch (e) { setMsg(e.response?.data?.detail || 'Failed'); }
    };
    if (!s) return <div className="spinner"/>;
    return (
        <Section title="Settings">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Company Name" value={s.company_name} onChange={(v) => setS({ ...s, company_name: v })} />
                <Input label="Tagline" value={s.tagline} onChange={(v) => setS({ ...s, tagline: v })} />
                <Input label="Contact Phone" value={s.contact_phone} onChange={(v) => setS({ ...s, contact_phone: v })} />
                <Input label="Contact Email" value={s.contact_email} onChange={(v) => setS({ ...s, contact_email: v })} />
                <Input label="Address" value={s.address} onChange={(v) => setS({ ...s, address: v })} />
                <div className="md:col-span-2 p-3 rounded-xl border border-sdf-line bg-sdf-cream/40">
                    <div className="text-[10px] font-bold text-sdf-red tracking-widest mb-1">WHATSAPP BUSINESS NUMBER</div>
                    <div className="text-xs text-sdf-text-2 mb-2">Enter with country code and no spaces (e.g. <code className="font-mono">+919XXXXXXXXX</code>). The distributor site&apos;s navigation menu &ldquo;Chat on WhatsApp&rdquo; button will use this number automatically.</div>
                    <input value={s.whatsapp || ''} onChange={(e) => setS({ ...s, whatsapp: e.target.value })} placeholder="+919XXXXXXXXX" className="w-full border border-sdf-line rounded-xl px-3 py-2.5 outline-none text-sm bg-white" data-testid="settings-whatsapp" />
                </div>
                <Input label="Instagram" value={s.instagram} onChange={(v) => setS({ ...s, instagram: v })} />
                <Input label="Facebook" value={s.facebook} onChange={(v) => setS({ ...s, facebook: v })} />
                <Input label="YouTube" value={s.youtube} onChange={(v) => setS({ ...s, youtube: v })} />
            </div>
            {mode === 'super_admin' && (
                <div className="mt-4 p-3 rounded-xl border border-sdf-line bg-sdf-cream/60">
                    <div className="text-xs font-bold text-sdf-red tracking-widest">PRIVATE (Super Admin Only)</div>
                    <div className="mt-1 text-xs text-sdf-text-2">Stored for future use, never shown on public site or invoices.</div>
                    <Input label="GST Number" value={s.gst_number || ''} onChange={(v) => setS({ ...s, gst_number: v.toUpperCase() })} />
                </div>
            )}
            <div className="mt-3 flex items-center gap-2">
                <button onClick={save} className="btn-red" data-testid="settings-save">Save Settings</button>
                {msg && <div className="text-xs text-green-700">{msg}</div>}
            </div>
        </Section>
    );
}

function AuditTab({ realm }) {
    const { data, loading } = useList(realm, '/admin/audit-log');
    return (
        <Section title="Audit Log">
            {loading ? <div className="spinner"/> : data.length === 0 ? <div className="text-sm text-sdf-text-2">No audit entries yet.</div> : (
                <div className="space-y-1">
                    {data.map(e => (
                        <div key={e.id} className="card-soft p-2 text-xs">
                            <div className="font-bold">{e.entity} · {e.actor_role}</div>
                            <div className="text-sdf-text-2">{new Date(e.at).toLocaleString()}</div>
                            <div className="text-sdf-text-2 truncate">{JSON.stringify(e.new)}</div>
                        </div>
                    ))}
                </div>
            )}
        </Section>
    );
}

function AdminsMgmtTab() {
    const realm = 'super_admin';
    const { data, loading, reload } = useList(realm, '/superadmin/admins');
    const [email, setEmail] = useState(''); const [displayName, setDisplayName] = useState('Admin'); const [tempPassword, setTempPassword] = useState('');
    const create = async () => {
        if (!email || !tempPassword) return;
        await authed(realm, { method: 'POST', url: '/superadmin/admins', data: { email, display_name: displayName, temp_password: tempPassword, permissions: [] } });
        setEmail(''); setTempPassword(''); reload();
    };
    const setActive = async (id, active) => { await authed(realm, { method: 'PUT', url: `/superadmin/admins/${id}`, data: { active } }); reload(); };
    const del = async (id) => { await authed(realm, { method: 'DELETE', url: `/superadmin/admins/${id}` }); reload(); };
    return (
        <Section title="Admin User Management">
            <div className="card-soft p-3 mb-3 grid grid-cols-1 md:grid-cols-4 gap-2">
                <Input label="Email" value={email} onChange={setEmail} />
                <Input label="Display Name" value={displayName} onChange={setDisplayName} />
                <Input label="Temp Password" value={tempPassword} onChange={setTempPassword} />
                <button onClick={create} className="btn-red self-end" data-testid="sa-create-admin">Create Admin</button>
            </div>
            {loading ? <div className="spinner"/> : (
                <div className="space-y-1">
                    {data.map(a => (
                        <div key={a.id} className="card-soft p-2 flex items-center gap-2">
                            <div className="flex-1"><div className="font-bold">{a.display_name}</div><div className="text-xs text-sdf-text-2">{a.email}</div></div>
                            <button onClick={() => setActive(a.id, !a.active)} className="btn-outline">{a.active ? 'Deactivate' : 'Activate'}</button>
                            <button onClick={() => del(a.id)} className="p-2 text-red-600"><Trash2 size={14}/></button>
                        </div>
                    ))}
                </div>
            )}
        </Section>
    );
}

function SecurityTab({ onEnabledChange }) {
    const realm = 'super_admin';
    const [status, setStatus] = useState({ totp_enabled: false });
    const [me, setMe] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [setupData, setSetupData] = useState(null); // { qr_data_url, secret_b32 }
    const [code, setCode] = useState('');
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);
    // Disable flow
    const [showDisable, setShowDisable] = useState(false);
    const [disPw, setDisPw] = useState('');
    const [disCode, setDisCode] = useState('');

    const refresh = async () => {
        try {
            const r = await authed(realm, { method: 'GET', url: '/superadmin/me' });
            setStatus(r.data);
            setMe(r.data);
        } catch (e) { /* ignore */ }
        try {
            const r = await authed(realm, { method: 'GET', url: '/superadmin/admins' });
            setEmployees(r.data || []);
        } catch (e) { /* ignore */ }
    };
    useEffect(() => { refresh(); }, []);  // eslint-disable-line

    const initSetup = async () => {
        setErr(''); setMsg(''); setBusy(true);
        try {
            const r = await authed(realm, { method: 'POST', url: '/superadmin/security/totp/init' });
            setSetupData(r.data);
        } catch (e) { setErr(e.response?.data?.detail || 'Setup failed'); }
        finally { setBusy(false); }
    };
    const verifyEnable = async () => {
        setErr(''); setMsg(''); setBusy(true);
        try {
            await authed(realm, { method: 'POST', url: '/superadmin/security/totp/enable', data: { totp_code: code.trim() } });
            setSetupData(null); setCode(''); setMsg('Google Authenticator activated successfully.');
            await refresh(); onEnabledChange?.(true);
        } catch (e) { setErr(e.response?.data?.detail || 'Verification failed'); }
        finally { setBusy(false); }
    };
    const disable = async () => {
        setErr(''); setMsg(''); setBusy(true);
        try {
            await authed(realm, { method: 'POST', url: '/superadmin/security/totp/disable', data: { password: disPw, totp_code: disCode.trim() } });
            setShowDisable(false); setDisPw(''); setDisCode('');
            setMsg('Google Authenticator has been disabled.');
            await refresh(); onEnabledChange?.(false);
        } catch (e) { setErr(e.response?.data?.detail || 'Failed to disable'); }
        finally { setBusy(false); }
    };

    return (
        <Section title="Security">
            <div className="space-y-6">
                {/* --- 2FA / Google Authenticator management --- */}
                <div>
                    <h3 className="text-sm font-extrabold uppercase tracking-widest text-sdf-text mb-3">Two-Factor Authentication</h3>
                    <div className={`p-4 rounded-2xl border ${status.totp_enabled ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`} data-testid="totp-status">
                        <div className="text-[10px] font-bold tracking-widest">STATUS</div>
                        <div className="text-lg font-extrabold mt-1">
                            {status.totp_enabled ? 'Google Authenticator is ENABLED' : 'Google Authenticator is NOT configured'}
                        </div>
                        <div className="text-xs text-sdf-text-2 mt-1">
                            {status.totp_enabled
                                ? 'Two-Factor Authentication is required at every Super Admin sign-in.'
                                : 'Currently only email + password are required to sign in. Enable 2FA to strengthen account security.'}
                        </div>
                    </div>

                    {!status.totp_enabled && !setupData && (
                        <button onClick={initSetup} disabled={busy} className="btn-red mt-3" data-testid="totp-init-btn">
                            {busy ? 'Preparing\u2026' : 'Enable Google Authenticator'}
                        </button>
                    )}

                    {!status.totp_enabled && setupData && (
                        <div className="card-soft p-4 space-y-3 mt-3">
                            <div className="text-sdf-red font-bold text-xs tracking-widest">SET UP GOOGLE AUTHENTICATOR</div>
                            <p className="text-xs text-sdf-text-2">Scan this QR code in the <b>Google Authenticator</b> app, then enter the 6-digit code below to activate.</p>
                            <div className="flex flex-col md:flex-row gap-4 items-center">
                                <img src={setupData.qr_data_url} alt="QR code" className="w-52 h-52 bg-white p-2 rounded-xl border border-sdf-line" />
                                <div className="text-xs text-sdf-text-2">
                                    <div className="font-bold text-sdf-text mb-1">Manual Secret Key</div>
                                    <code className="font-mono bg-neutral-100 px-2 py-1 rounded text-[11px] break-all" data-testid="totp-secret">{setupData.secret_b32}</code>
                                    <div className="mt-2">Issuer: <b>{setupData.issuer}</b></div>
                                    <div>Account: <b>{setupData.account}</b></div>
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-sdf-text-2 tracking-widest">VERIFICATION CODE</label>
                                <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} maxLength={6} className="mt-1.5 w-full border border-sdf-line rounded-xl px-3 py-3 outline-none text-sm bg-white tracking-widest font-bold" data-testid="totp-code-input" />
                            </div>
                            {err && <div className="text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2">{err}</div>}
                            <div className="flex items-center gap-2">
                                <button onClick={verifyEnable} disabled={busy || code.length !== 6} className="btn-red" data-testid="totp-verify-btn">{busy ? 'Verifying\u2026' : 'Verify & Activate'}</button>
                                <button onClick={() => { setSetupData(null); setCode(''); setErr(''); }} className="btn-outline">Cancel</button>
                            </div>
                        </div>
                    )}

                    {status.totp_enabled && !showDisable && (
                        <div className="flex items-center gap-2 mt-3">
                            <button onClick={() => setShowDisable(true)} className="btn-outline" data-testid="totp-disable-toggle">Disable Google Authenticator</button>
                        </div>
                    )}
                    {status.totp_enabled && showDisable && (
                        <div className="card-soft p-4 space-y-3 mt-3">
                            <div className="text-sdf-red font-bold text-xs tracking-widest">DISABLE 2FA</div>
                            <p className="text-xs text-sdf-text-2">Enter your current password and a valid authenticator code to disable 2FA.</p>
                            <div>
                                <label className="text-[10px] font-bold text-sdf-text-2 tracking-widest">CURRENT PASSWORD</label>
                                <input type="password" value={disPw} onChange={(e) => setDisPw(e.target.value)} className="mt-1.5 w-full border border-sdf-line rounded-xl px-3 py-3 outline-none text-sm bg-white" data-testid="totp-disable-pw" />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-sdf-text-2 tracking-widest">AUTHENTICATOR CODE</label>
                                <input value={disCode} onChange={(e) => setDisCode(e.target.value.replace(/\D/g, ''))} maxLength={6} className="mt-1.5 w-full border border-sdf-line rounded-xl px-3 py-3 outline-none text-sm bg-white tracking-widest font-bold" data-testid="totp-disable-code" />
                            </div>
                            {err && <div className="text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2">{err}</div>}
                            <div className="flex items-center gap-2">
                                <button onClick={disable} disabled={busy} className="btn-red">{busy ? 'Disabling\u2026' : 'Confirm Disable'}</button>
                                <button onClick={() => { setShowDisable(false); setErr(''); }} className="btn-outline">Cancel</button>
                            </div>
                        </div>
                    )}
                    {msg && <div className="text-sm text-green-700 rounded bg-green-50 border border-green-200 px-3 py-2 mt-3" data-testid="totp-success">{msg}</div>}
                    {err && !setupData && !showDisable && <div className="text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2 mt-3">{err}</div>}
                </div>

                <div className="h-px bg-sdf-line" />

                {/* --- Super Admin — own credentials --- */}
                <div>
                    <h3 className="text-sm font-extrabold uppercase tracking-widest text-sdf-text mb-3">Super Admin Credentials</h3>
                    <div className="grid grid-cols-1 gap-4">
                        <ChangeMyPasswordCard realm={realm} totpEnabled={!!status.totp_enabled} />
                        <ChangeMyEmailCard realm={realm} totpEnabled={!!status.totp_enabled} currentEmail={me?.email || ''} onUpdated={refresh} />
                    </div>
                </div>

                <div className="h-px bg-sdf-line" />

                {/* --- Employee Admin credentials --- */}
                <div>
                    <h3 className="text-sm font-extrabold uppercase tracking-widest text-sdf-text mb-3">Employee Admin Credentials</h3>
                    {employees.length === 0 ? (
                        <div className="card-soft p-4 text-sm text-sdf-text-2" data-testid="emp-empty">No Employee Admin accounts exist yet. Create one from the <b>Admins</b> tab first.</div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4">
                            <ChangeEmployeePasswordCard employees={employees} onDone={refresh} />
                            <ChangeEmployeeEmailCard employees={employees} onDone={refresh} />
                        </div>
                    )}
                </div>
            </div>
        </Section>
    );
}


/**
 * FaceImageControl — Batch B2 Bug-Fix Pass 3, Feature 2
 *
 * A dedicated Face Image editor for a single weight/size variant. When
 * `variant.face_image_url` is empty, the card shows the first flavour's image
 * with a "using auto-fallback" hint so the admin knows it's not a manual pick.
 * When set, the manual override is shown with a "Remove" button that reverts
 * to auto-fallback.
 */
const FaceImageControl = ({ realm, variant, firstFlavourImage, onSet, onClear }) => {
    const hasManual = !!(variant.face_image_url && variant.face_image_url.trim());
    const displayed = hasManual ? variant.face_image_url : firstFlavourImage;
    return (
        <div className="mt-3 p-3 rounded-2xl border border-sdf-line bg-white flex items-start gap-3" data-testid={`face-image-${variant.size_label}`}>
            <div className="w-20 h-20 rounded-xl overflow-hidden border border-sdf-line bg-neutral-100 shrink-0 flex items-center justify-center">
                {displayed ? (
                    <img src={absUrl(displayed)} alt="Face" className="w-full h-full object-cover" />
                ) : (
                    <ImageIcon size={20} className="text-neutral-400" />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[10px] font-bold text-sdf-red tracking-widest">FACE IMAGE</div>
                    <span
                        className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${hasManual ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}
                        data-testid={`face-image-status-${variant.size_label}`}
                    >
                        {hasManual ? 'MANUAL' : 'AUTO (first flavour)'}
                    </span>
                </div>
                <div className="text-[11px] text-sdf-text-2 mt-1">
                    {hasManual
                        ? 'This image is shown to distributors on the product-listing card for this size.'
                        : firstFlavourImage
                            ? 'No manual pick — using the first flavour image automatically.'
                            : 'No manual pick and no flavour has an image yet. Set one to display a face.'}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <ImagePickerButton
                        realm={realm}
                        subdir="variants"
                        label={hasManual ? 'Change Face Image' : 'Set Face Image'}
                        variant="outline"
                        onPicked={onSet}
                        testIdPrefix={`face-picker-${variant.size_label}`}
                    />
                    {hasManual && (
                        <button
                            type="button"
                            onClick={onClear}
                            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                            data-testid={`face-image-clear-${variant.size_label}`}
                            title="Revert to auto (first flavour image)"
                        >
                            <RotateCcw size={12} /> Remove
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
